import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'

import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import ResourceFieldCollection from 'modules/custom_resources/store/collections/customFields/resourceFields'
import CustomFieldValuesCollection from 'modules/custom_resources/store/collections/customFields/values'
import CustomFieldModel from 'modules/custom_resources/store/models/customFields/Field'
import type Employee from 'modules/employees/store/models/Employee'

interface resourceFieldsProps {
  fieldableId: number
  contractVersion: ContractVersion | undefined
  employee?: Employee
}

export const useFetchCustomFields = ({
  fieldableId,
  contractVersion,
  employee,
}: resourceFieldsProps) => {
  const usageGroups = [CustomFieldModel.UsageGroups.contractVersions]
  const fieldableModelRef = 'legal_entity'
  const valuableModelRef = 'Contracts::ContractVersion'

  const employeeId = employee?.attributes.id || null

  const resourceFieldsCollection = useLazyRef(
    () =>
      new ResourceFieldCollection([], {
        model_ref: fieldableModelRef,
        instance_id: fieldableId,
      })
  ).current

  const valuesCollection = useLazyRef(
    () =>
      new CustomFieldValuesCollection([], {
        model_ref: valuableModelRef,
        instance_id: contractVersion?.id,
      })
  ).current

  const { loading } = useAsyncData(() => {
    resourceFieldsCollection.config = {
      model_ref: fieldableModelRef,
      instance_id: fieldableId.toString(),
    }

    const promises = [resourceFieldsCollection.byUsageGroup(usageGroups, employeeId)]
    if (contractVersion) {
      valuesCollection.config = {
        model_ref: valuableModelRef,
        instance_id: contractVersion.id,
      }
      promises.push(valuesCollection.byUsageGroup(usageGroups))
    }

    return Promise.all(promises)
  }, [contractVersion?.id, fieldableId, employeeId])

  return {
    loading: loading,
    resourceFields: resourceFieldsCollection.models,
    valuesCollection: valuesCollection.models,
  }
}
